import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadCsv } from "@/lib/exports";
import { formatDate, formatDateTime } from "@/lib/utils-format";
import { Truck, DownloadSimple, Printer, Plus, Trash } from "@phosphor-icons/react";

export default function Manifests() {
  const [manifests, setManifests] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openView, setOpenView] = useState(null);

  const load = async () => {
    const r = await api.get("/manifests");
    setManifests(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this manifest? Orders will be un-linked.")) return;
    try { await api.delete(`/manifests/${id}`); load(); toast.success("Manifest deleted"); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="manifests-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Shipping</p>
          <h1 className="text-4xl font-black tracking-tighter">Manifests</h1>
          <p className="text-sm text-zinc-500 mt-2">Batch hand-off documents for FedEx / DHL / UPS pickup.</p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-manifest-button">
          <Plus size={16} weight="bold" className="mr-1" /> New Manifest
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Manifest #</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Courier</th>
              <th className="text-right px-4 py-2.5">Orders</th>
              <th className="text-right px-4 py-2.5">Packages</th>
              <th className="text-right px-4 py-2.5">Weight (kg)</th>
              <th className="text-right px-4 py-2.5">Declared Value</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {manifests.map((m) => (
              <tr key={m.manifest_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`manifest-row-${m.manifest_id}`}>
                <td className="px-4 py-3 font-mono text-sm">{m.manifest_number}</td>
                <td className="px-4 py-3 text-sm">{formatDate(m.manifest_date)}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-[11px] uppercase tracking-widest font-semibold bg-zinc-100 border border-zinc-300 rounded">{m.courier}</span></td>
                <td className="px-4 py-3 text-right font-mono text-sm">{m.rows?.length || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{m.total_packages}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{m.total_weight}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">€{Number(m.total_declared_value).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setOpenView(m)} className="p-1 hover:bg-zinc-200 rounded" title="Print / view" data-testid={`view-manifest-${m.manifest_id}`}>
                      <Printer size={14} />
                    </button>
                    <button onClick={() => downloadCsv(`/manifests/${m.manifest_id}/csv`, `${m.manifest_number}.csv`)} className="p-1 hover:bg-zinc-200 rounded" title="Download CSV" data-testid={`csv-manifest-${m.manifest_id}`}>
                      <DownloadSimple size={14} />
                    </button>
                    <button onClick={() => remove(m.manifest_id)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded" title="Delete" data-testid={`delete-manifest-${m.manifest_id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {manifests.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No manifests yet. Click "New Manifest" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <NewManifestDialog open={openNew} onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); load(); }} />
      {openView && <ViewManifestDialog manifest={openView} onClose={() => setOpenView(null)} />}
    </div>
  );
}

function NewManifestDialog({ open, onClose, onDone }) {
  const [courier, setCourier] = useState("FedEx");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    api.get("/manifests/candidates", { params: { courier } }).then((r) => setCandidates(r.data));
  }, [open, courier]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.order_id)));
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Select at least one order"); return; }
    setBusy(true);
    try {
      const r = await api.post("/manifests", { courier, order_ids: [...selected], notes });
      toast.success(`Manifest ${r.data.manifest_number} created (${selected.size} orders)`);
      onDone();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-white" data-testid="new-manifest-dialog">
        <DialogHeader><DialogTitle>New Shipping Manifest</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Courier</Label>
              <Select value={courier} onValueChange={setCourier}>
                <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="manifest-courier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="border-zinc-300 rounded-md mt-1" data-testid="manifest-notes-input" />
            </div>
          </div>

          <div className="border border-zinc-200 max-h-[380px] overflow-y-auto" data-testid="manifest-candidates-table">
            <table className="w-full data-table">
              <thead className="sticky top-0 bg-zinc-50">
                <tr>
                  <th className="text-left px-3 py-2 w-8">
                    <input type="checkbox" checked={selected.size === candidates.length && candidates.length > 0} onChange={toggleAll} data-testid="manifest-select-all" />
                  </th>
                  <th className="text-left px-3 py-2">Order #</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Tracking</th>
                  <th className="text-left px-3 py-2">Country</th>
                  <th className="text-right px-3 py-2">Weight</th>
                  <th className="text-right px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.order_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`candidate-row-${c.order_id}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={selected.has(c.order_id)} onChange={() => toggle(c.order_id)} data-testid={`candidate-check-${c.order_id}`} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{c.order_number}</td>
                    <td className="px-3 py-2 text-xs">{c.customer_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.tracking_number || "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.country}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono">{c.weight || 0}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono">{c.total_amount} {c.currency}</td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-sm text-zinc-500">No ready-to-ship / shipped orders for {courier} available.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
            <p className="text-sm text-zinc-600" data-testid="manifest-selected-count">{selected.size} selected</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
              <Button onClick={submit} disabled={busy || selected.size === 0} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="create-manifest-submit">
                <Truck size={14} weight="bold" className="mr-1" /> Create Manifest
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewManifestDialog({ manifest, onClose }) {
  const printMe = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const doc = win.document;
    doc.title = manifest.manifest_number;

    // Build the whole document via DOM APIs so no interpolated string is ever parsed as HTML.
    // (Fixes XSS if a customer name or manifest field contains angle brackets.)
    const style = doc.createElement("style");
    style.textContent = `body{font-family:Arial,sans-serif;padding:24px;color:#09090B}
      table{width:100%;border-collapse:collapse;font-size:11px;margin-top:16px}
      th,td{border:1px solid #D4D4D8;padding:6px 8px;text-align:left}
      th{background:#F4F4F5;text-transform:uppercase;letter-spacing:0.1em;font-size:9px}
      h1{margin:0;letter-spacing:-0.02em}
      .meta{display:flex;justify-content:space-between;margin:16px 0;padding:12px;background:#F4F4F5;border:1px solid #D4D4D8}
      .meta div{font-size:11px}.label{text-transform:uppercase;letter-spacing:0.15em;font-size:9px;color:#71717A}
      .totals{margin-top:12px;text-align:right;font-size:12px}
      .sig{margin-top:48px;display:grid;grid-template-columns:1fr 1fr;gap:32px}
      .sig-line{border-bottom:1px solid #09090B;height:40px;margin-bottom:4px}`;
    doc.head.appendChild(style);

    const el = (tag, opts = {}, children = []) => {
      const n = doc.createElement(tag);
      if (opts.className) n.className = opts.className;
      if (opts.text != null) n.textContent = String(opts.text);
      children.forEach((c) => c && n.appendChild(c));
      return n;
    };

    const body = doc.body;
    body.appendChild(el("h1", { text: `Shipping Manifest ${manifest.manifest_number}` }));

    const meta = el("div", { className: "meta" });
    [
      ["Courier", manifest.courier],
      ["Manifest Date", manifest.manifest_date],
      ["Total Packages", manifest.total_packages],
      ["Total Weight (kg)", manifest.total_weight],
    ].forEach(([label, val]) => {
      const cell = el("div");
      cell.appendChild(el("div", { className: "label", text: label }));
      cell.appendChild(el("strong", { text: val }));
      meta.appendChild(cell);
    });
    body.appendChild(meta);

    const table = el("table");
    const thead = el("thead");
    const trh = el("tr");
    ["#", "Order", "Tracking", "Customer", "Country", "Packages", "Weight", "Value"].forEach((h) =>
      trh.appendChild(el("th", { text: h }))
    );
    thead.appendChild(trh);
    table.appendChild(thead);

    const tbody = el("tbody");
    (manifest.rows || []).forEach((r, i) => {
      const tr = el("tr");
      [i + 1, r.order_number, r.tracking_number || "", r.customer_name, r.country, r.packages, r.weight, `${r.declared_value} ${r.currency}`].forEach((v) =>
        tr.appendChild(el("td", { text: v }))
      );
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    body.appendChild(table);

    const totals = el("div", { className: "totals" });
    totals.appendChild(doc.createTextNode("Total declared value: "));
    totals.appendChild(el("strong", { text: `€${Number(manifest.total_declared_value).toLocaleString()}` }));
    body.appendChild(totals);

    const sig = el("div", { className: "sig" });
    ["Handed over by", `Received by (${manifest.courier} driver)`].forEach((label) => {
      const box = el("div");
      box.appendChild(el("div", { className: "sig-line" }));
      box.appendChild(el("div", { className: "label", text: label }));
      sig.appendChild(box);
    });
    body.appendChild(sig);

    win.print();
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-white" data-testid="view-manifest-dialog">
        <DialogHeader><DialogTitle>{manifest.manifest_number} · {manifest.courier}</DialogTitle></DialogHeader>
        <div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Stat label="Orders" value={manifest.rows.length} />
            <Stat label="Packages" value={manifest.total_packages} />
            <Stat label="Weight (kg)" value={manifest.total_weight} />
            <Stat label="Value (€)" value={Number(manifest.total_declared_value).toLocaleString()} />
          </div>
          <div className="border border-zinc-200 max-h-[300px] overflow-y-auto">
            <table className="w-full data-table">
              <thead className="sticky top-0 bg-zinc-50"><tr><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Tracking</th><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Country</th></tr></thead>
              <tbody>
                {manifest.rows.map((r) => (
                  <tr key={r.order_id} className="border-b border-zinc-100"><td className="px-3 py-2 font-mono text-xs">{r.order_number}</td><td className="px-3 py-2 font-mono text-xs">{r.tracking_number || "—"}</td><td className="px-3 py-2 text-xs">{r.customer_name}</td><td className="px-3 py-2 text-xs">{r.country}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3 font-mono">Generated by {manifest.generated_by} · {formatDateTime(manifest.created_at)}</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => downloadCsv(`/manifests/${manifest.manifest_id}/csv`, `${manifest.manifest_number}.csv`)} className="border-zinc-300 rounded-md" data-testid="download-manifest-csv">
              <DownloadSimple size={14} weight="bold" className="mr-1" /> Download CSV
            </Button>
            <Button onClick={printMe} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="print-manifest">
              <Printer size={14} weight="bold" className="mr-1" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 p-3 bg-zinc-50">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
      <p className="text-xl font-black tracking-tighter font-mono mt-1">{value}</p>
    </div>
  );
}
